import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Container } from '../components/primitives/Container';
import { Eyebrow } from '../components/primitives/Eyebrow';
import { Tag } from '../components/primitives/Tag';
import { useArticle } from '../content/load';
import { formatDate } from '../lib/format';
import { useDocumentTitle } from '../lib/seo';
import type { ArticleBodyBlock } from '../content/schemas';

const Wrap = styled.article`
  max-width: 720px;
  margin: 0 auto;
  padding: ${(p) => p.theme.step[7]}px ${(p) => p.theme.layout.gutter}px;
`;

const Title = styled.h1`
  font-family: ${(p) => p.theme.font.serif};
  font-size: clamp(${(p) => p.theme.step[7]}px, 5vw, ${(p) => p.theme.step[10]}px);
  letter-spacing: -0.02em;
  margin: 12px 0;
  line-height: 1.1;
`;

const Lead = styled.p`
  font-size: ${(p) => p.theme.step[5]}px;
  color: ${(p) => p.theme.color.text[1]};
  line-height: 1.55;
  font-family: ${(p) => p.theme.font.serif};
`;

const Meta = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  font-family: ${(p) => p.theme.font.mono};
  font-size: ${(p) => p.theme.step[1]}px;
  color: ${(p) => p.theme.color.text[3]};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-top: 8px;
`;

const Body = styled.div`
  margin-top: ${(p) => p.theme.step[7]}px;
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.theme.step[4]}px;

  p {
    font-size: ${(p) => p.theme.step[4]}px;
    color: ${(p) => p.theme.color.text[1]};
    line-height: 1.7;
    margin: 0;
  }

  h2, h3, h4 {
    font-family: ${(p) => p.theme.font.serif};
    margin: ${(p) => p.theme.step[4]}px 0 0;
    letter-spacing: -0.01em;
  }

  blockquote {
    border-left: 2px solid ${(p) => p.theme.color.accent.amber};
    padding-left: 16px;
    color: ${(p) => p.theme.color.text[0]};
    font-family: ${(p) => p.theme.font.serif};
    font-size: ${(p) => p.theme.step[5]}px;
    line-height: 1.4;
    margin: 0;
  }

  pre {
    background: ${(p) => p.theme.color.bg[1]};
    border: 1px solid ${(p) => p.theme.color.border.soft};
    border-radius: 10px;
    padding: 16px;
    overflow: auto;
    font-family: ${(p) => p.theme.font.mono};
    font-size: ${(p) => p.theme.step[2]}px;
    color: ${(p) => p.theme.color.text[1]};
  }
`;

function renderBlock(b: ArticleBodyBlock, i: number) {
  if (b.kind === 'heading') {
    const Tag = (`h${b.level ?? 2}` as keyof JSX.IntrinsicElements);
    return <Tag key={i}>{b.text}</Tag>;
  }
  if (b.kind === 'paragraph') return <p key={i}>{b.text}</p>;
  if (b.kind === 'quote') return <blockquote key={i}>{b.text}</blockquote>;
  if (b.kind === 'code')
    return (
      <pre key={i}>
        <code>{b.text}</code>
      </pre>
    );
  if (b.kind === 'figure')
    return (
      <figure key={i}>
        <figcaption>{b.caption}</figcaption>
      </figure>
    );
  return null;
}

export default function Article() {
  const { id = '' } = useParams<{ id: string }>();
  const a = useArticle(id);
  useDocumentTitle(a.title, a.lead);
  return (
    <Container>
      <Wrap>
        <Eyebrow $variant="amber">{a.cat.toUpperCase()}</Eyebrow>
        <Title>{a.title}</Title>
        <Meta>
          <Tag>{a.cat}</Tag>
          <span>{a.author}</span>
          <span>·</span>
          <span>{formatDate(a.date)}</span>
          <span>·</span>
          <span>{a.read} MIN</span>
        </Meta>
        <Lead style={{ marginTop: 24 }}>{a.lead}</Lead>
        {a.body ? (
          <Body>{a.body.map(renderBlock)}</Body>
        ) : (
          <p style={{ opacity: 0.6, fontStyle: 'italic', marginTop: 24 }}>
            Full article coming soon.
          </p>
        )}
      </Wrap>
    </Container>
  );
}
